/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash/get';
import { injectIntl, FormattedMessage } from 'react-intl';

import FilmInfoSection from '../partials/FilmInfoSection';
import Links from '../partials/Links';
import BoxButton from '../partials/BoxButton';
import SocialButtons from '../partials/SocialButtons';

import styles from '../../../styles/blocks/film-info.scss';

const propTypes = {
    intl: PropTypes.shape({
        locale: PropTypes.string,
    }).isRequired,
    url: PropTypes.string,
    synopsis: PropTypes.objectOf(PropTypes.string),
    genre: PropTypes.objectOf(PropTypes.string),
    language: PropTypes.objectOf(PropTypes.string),
    year: PropTypes.objectOf(PropTypes.string),
    awards: PropTypes.objectOf(PropTypes.string),
    credits: PropTypes.objectOf(PropTypes.string),
    partners: PropTypes.objectOf(PropTypes.string),
    broadcasters: PropTypes.objectOf(PropTypes.string),
    distribution: PropTypes.objectOf(PropTypes.string),
    poster: PropTypes.shape({
        url: PropTypes.string,
    }),
    dvdLink: PropTypes.objectOf(PropTypes.string),
    streamLink: PropTypes.objectOf(PropTypes.string),
};

const defaultProps = {
    url: '',
    synopsis: null,
    genre: null,
    language: null,
    year: null,
    awards: null,
    credits: null,
    partners: null,
    broadcasters: null,
    distribution: null,
    poster: null,
    dvdLink: null,
    streamLink: null,
};

const FilmInfoBlock = ({ intl, url, ...props }) => {
    const { locale } = intl;
    const synopsis = get(props, `synopsis.${locale}`, null);
    const language = get(props, `language.${locale}`, null);
    const genre = get(props, `genre.${locale}`, null);
    const year = get(props, `year.${locale}`, get(props, 'year.en', get(props, 'year.fr', null)));
    const awards = get(props, `awards.${locale}`, null);
    const credits = get(props, `credits.${locale}`, null);
    const partners = get(props, `partners.${locale}`, null);
    const broadcasters = get(props, `broadcasters.${locale}`, null);
    const distribution = get(props, `distribution.${locale}`, null);
    const poster = get(props, `poster.${locale}.url`, null);
    const dvdLink = get(props, `dvdLink.${locale}`, null);
    const streamLink = get(props, `streamLink.${locale}`, null);
    const links = get(props, 'links', [])
        .map(it => ({
            label: get(it, `label.${locale}`, get(it, 'label.en', get(it, 'label.fr', null))),
            url: get(it, `url.${locale}`, get(it, 'url.en', get(it, 'url.fr', null))),
        }))
        .filter(it => it.url !== null && it.url.length > 0);
    return (
        <div
            className={classNames({
                [styles.container]: true,
            })}
        >
            <div className={styles.inner}>
                <div className={styles.cols}>
                    <div
                        className={classNames({
                            [styles.col]: true,
                            [styles.colSocial]: true,
                        })}
                    >
                        <div className={styles.social}>
                            <SocialButtons url={url} />
                        </div>
                    </div>
                    <div
                        className={classNames({
                            [styles.col]: true,
                            [styles.colContent]: true,
                        })}
                    >
                        <div className={styles.cols}>
                            <div className={styles.col}>
                                {synopsis !== null ? (
                                    <FilmInfoSection
                                        label={{
                                            id: 'content.synopsis_label',
                                            defaultMessage: 'Synopsis',
                                        }}
                                        text={synopsis}
                                        className={classNames({
                                            [styles.synopsis]: true,
                                            [styles.section]: true,
                                        })}
                                    />
                                ) : null}

                                {year !== null ? (
                                    <FilmInfoSection
                                        label={{
                                            id: 'content.production_year_label',
                                            defaultMessage: 'Year of Production',
                                        }}
                                        text={year}
                                        className={styles.section}
                                    />
                                ) : null}

                                {genre !== null ? (
                                    <FilmInfoSection
                                        label={{
                                            id: 'content.genre_label',
                                            defaultMessage: 'Genre',
                                        }}
                                        text={genre}
                                        className={styles.section}
                                    />
                                ) : null}

                                {language !== null ? (
                                    <FilmInfoSection
                                        label={{
                                            id: 'content.language_label',
                                            defaultMessage: 'Language',
                                        }}
                                        text={language}
                                        className={styles.section}
                                    />
                                ) : null}

                                {partners !== null ? (
                                    <FilmInfoSection
                                        label={{
                                            id: 'content.partners_label',
                                            defaultMessage: 'Partners',
                                        }}
                                        text={partners}
                                        className={styles.section}
                                    />
                                ) : null}

                                {broadcasters !== null ? (
                                    <FilmInfoSection
                                        label={{
                                            id: 'content.broadcasters_label',
                                            defaultMessage: 'Broadcasters',
                                        }}
                                        text={broadcasters}
                                        className={styles.section}
                                    />
                                ) : null}

                                {distribution !== null ? (
                                    <FilmInfoSection
                                        label={{
                                            id: 'content.distribution_label',
                                            defaultMessage: 'Distribution',
                                        }}
                                        text={distribution}
                                        className={styles.section}
                                    />
                                ) : null}

                                {credits !== null ? (
                                    <FilmInfoSection
                                        label={{
                                            id: 'content.credits_label',
                                            defaultMessage: 'Main credits',
                                        }}
                                        text={credits}
                                        className={styles.section}
                                    />
                                ) : null}

                                {awards !== null ? (
                                    <FilmInfoSection
                                        label={{
                                            id: 'content.awards_label',
                                            defaultMessage: 'Awards & festivals',
                                        }}
                                        text={awards}
                                        className={styles.section}
                                    />
                                ) : null}

                                {links !== null ? (
                                    <Links items={links} className={styles.section} />
                                ) : null}
                            </div>
                            {poster !== null || dvdLink !== null || streamLink !== null ? (
                                <div className={styles.col}>
                                    {poster !== null ? (
                                        <div className={styles.poster}>
                                            <img src={poster} className={styles.img} alt="" />
                                        </div>
                                    ) : null}
                                    <div className={styles.actions}>
                                        {dvdLink !== null ? (
                                            <BoxButton
                                                label={{
                                                    id: 'content.buy_dvd',
                                                    defaultMessage: 'Buy DVD',
                                                }}
                                                link={dvdLink}
                                                className={styles.button}
                                                external
                                                red
                                                small
                                            />
                                        ) : null}
                                        {streamLink !== null ? (
                                            <BoxButton
                                                label={{
                                                    id: 'content.stream_online',
                                                    defaultMessage: 'Stream online',
                                                }}
                                                link={streamLink}
                                                className={styles.button}
                                                external
                                                red
                                                small
                                            />
                                        ) : null}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

FilmInfoBlock.propTypes = propTypes;
FilmInfoBlock.defaultProps = defaultProps;

export default injectIntl(FilmInfoBlock);



// WEBPACK FOOTER //
// ./js/components/blocks/FilmInfo.jsx